<template>
  <div class="view-product-register-module-lesson">
    <div class="register-lesson-header">
      <snow-steps v-model="currentStep" :steps="steps" />
      <div class="d-flex justify-content-end align-items-center gap-2">
        <snow-button
          variant="gray"
          :text="currentStep === 1 ? 'Cancelar' : 'Voltar'"
          @click="back"
        />
        <div class="d-lg-flex d-none gap-2" v-if="currentStep === 2">
          <snow-button
            :disabled="!isValid"
            variant="outline"
            text="Salvar rascunho"
            @click="saveAsDraft"
          />
          <snow-button
            :disabled="!isValid"
            variant="outline"
            text="Salvar e criar novo"
            @click="next((createNew = true))"
          />
        </div>
        <snow-button
          :disabled="!isValid"
          :text="currentStep === steps.length ? 'Salvar' : 'Próximo'"
          @click="next((createNew = false))"
        />
      </div>

      <div class="d-lg-none d-flex gap-2" v-if="currentStep === 2">
        <snow-button
          :disabled="!isValid"
          variant="outline"
          text="Salvar rascunho"
          @click="saveAsDraft"
        />
        <snow-button
          :disabled="!isValid"
          variant="outline"
          text="Salvar e criar novo"
          @click="next((createNew = true))"
        />
      </div>
    </div>

    <div class="register-lesson-content">
      <div v-if="currentStep === 1" v-show="nav === 'info'">
        <register-lesson-basic
          :product-id="$route.params.id"
          :form="form"
          :errors="errors"
          :lesson="lesson"
        />
      </div>
      <div v-if="currentStep === 2" v-show="nav === 'info'">
        <register-lesson-customization
          :form="form"
          :errors="errors"
          :product-id="$route.params.id"
        />
      </div>
    </div>
  </div>
</template>

<script>
import RegisterLessonBasic from "@/components/product/details/content/RegisterLessonBasic";
import RegisterLessonCustomization from "@/components/product/details/content/RegisterLessonCustomization";
import { formFields, toComputerDate, toHumanDate } from "@/functions/helpers";
import MemberAreaMixin from "@/mixins/memberArea.js";
import get from "lodash/get";
import { mapState } from "vuex";

export default {
  mixins: [MemberAreaMixin],
  components: {
    RegisterLessonBasic,
    RegisterLessonCustomization
  },
  data() {
    return {
      currentStep: 1,
      steps: ["Informações básicas", "Personalização"],
      createNew: false,
      initial_extra_files: [],
      ...formFields(
        [
          "name",
          "link_media",
          "days_to_release",
          "meeting_password",
          "meeting_view_type",
          "release_at",
          "description",
          "duration",
          "extra_reading",
          "tags",
          "picture",
          "comments_enabled",
          "product_id",
          "module_id"
        ],
        {
          extra_reading: [],
          tags: [],
          comments_enabled: true,
          meeting_password: "",
          meeting_view_type: "speaker",
          module_id: get(this.$route, "query.module_id", undefined)
        }
      ),
      modal: false,
      nav: "info",
      params: {
        product_id: this.$route.params.id,
        lesson_id: this.$route.params.lesson
      },
      navOptions: [
        { id: "info", name: "Conteúdo" },
        { id: "comments", name: "Comentários" }
      ]
    };
  },
  computed: {
    ...mapState("product", ["product"]),
    ...mapState("membersLessons", ["lesson", "files"]),
    isValid() {
      return this.form.name && this.form.module_id;
    }
  },
  mounted() {
    if (this.$route.params.lesson) {
      this.loadInfo();
    }
  },
  methods: {
    loadInfo() {
      this.loading = true;
      this.$store
        .dispatch("membersLessons/getDetails", this.$route.params.lesson)
        .then(data => {
          this.form = {
            ...data,
            module_id: data.module.id,
            extra_reading: data.extra_reading || [],
            extra_files: data.extra_files || [],
            tags: data.tags || [],
            release_at: toHumanDate(data.release_at)
          };
          this.initial_extra_files = data.extra_files || [];
        })
        .finally(() => (this.loading = false));
    },
    async handleNewFiles(lesson_id) {
      const files_to_upload = get(this.form, "extra_files", []).filter(
        ({ id }) => !this.initial_extra_files.find(f => f.id == id)
      );
      if (files_to_upload.length) {
        try {
          await this.$store.dispatch("membersLessons/uploadFile", {
            files: files_to_upload,
            id: lesson_id
          });
        } catch (e) {
          const message =
            "Erro no upload dos arquivos, por favor tente novamente";
          throw new Error(message);
        }
      }
    },
    async handleRemovedFiles() {
      const files_to_remove = this.initial_extra_files.filter(
        ({ id }) => !get(this.form, "extra_files", []).find(f => f.id == id)
      );
      if (files_to_remove.length) {
        await Promise.all([
          ...files_to_remove.map(f =>
            this.$store.dispatch("membersLessons/deleteFile", f.id)
          )
        ]);
      }
    },
    async submit() {
      this.loading = true;

      const data = { ...this.form, product_id: this.params.product_id };
      let action = "membersLessons/post";

      if (this.params.lesson_id) {
        data.id = this.params.lesson_id;
        action = "membersLessons/put";
      }
      if (!this.form.days_to_release) {
        data.days_to_release = 0;
      }

      if (data.release_at) {
        data.release_at = toComputerDate(data.release_at);
      } else {
        data.release_at = null;
      }

      // Não enviar picture vazio (causa erro no backend)
      const isEmpty = val => val === "" || val == null || val === undefined;
      if (isEmpty(data.picture)) delete data.picture;

      // Tags deve ser array (backend espera lista, não string)
      data.tags = Array.isArray(data.tags)
        ? data.tags
        : typeof data.tags === "string"
        ? data.tags
            .split(",")
            .map(t => t.trim())
            .filter(Boolean)
        : [];

      // Duration deve ser inteiro válido
      const durationNum = parseInt(data.duration, 10);
      data.duration = Number.isNaN(durationNum) ? 0 : durationNum;

      try {
        const response = await this.$store.dispatch(action, data);
        await this.handleNewFiles(response.data.id);
        await this.handleRemovedFiles();

        this.$message.success("Salvo com sucesso");

        if (this.createNew) {
          this.$router.go(0);
        } else {
          this.$router.go(-1);
        }
      } catch (error) {
        const apiErrors = get(error, "response.data", {});
        if (apiErrors && typeof apiErrors === "object" && !apiErrors.detail) {
          this.errors = { ...this.errors, ...apiErrors };
          this.$message.error(
            "Verifique os campos destacados e corrija os erros."
          );
          if (apiErrors.picture) {
            this.currentStep = 2;
          }
        } else {
          const msg = get(
            error,
            "response.data.detail",
            get(error, "message", "Não foi possível salvar a aula.")
          );
          this.$message.error(msg);
        }
      } finally {
        this.loading = false;
      }
    },
    back() {
      if (this.currentStep === 1) {
        this.$router.go(-1);
      } else {
        this.currentStep--;
      }
    },
    saveAsDraft() {
      this.form.status = "draft";
      this.submit();
    },
    next() {
      if (this.currentStep === this.steps.length) {
        this.form.status = "published";
        this.submit();
      } else {
        this.currentStep++;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.view-product-register-module-lesson {
  .register-lesson-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 28px;
    gap: 16px;

    @media screen and (max-width: 991px) {
      flex-direction: column;
      align-items: stretch;

      button {
        width: 100%;
      }
    }
  }
  .register-lesson-content {
    background: var(--bg-2);
    padding: 24px;
    border-radius: 16px;
  }
}
</style>
